/**
 * Hyperliquid market data (mids + candles) for Oroboros terminal.
 * Candles use the same HL path as MiniApp Trade (`fetchTradeChartCandles`).
 */

import {
  fetchTradeChartCandles,
  type HLCandleInterval,
} from '../../miniapp/api/hyperliquidDirect';
import {
  coinToAllMidsQuery,
  lookupMidInMids,
  parseMidValue,
  unwrapAllMidsPayload,
} from './marketMids';

export {
  coinToAllMidsQuery,
  lookupMidInMids,
  unwrapAllMidsPayload,
} from './marketMids';

const HL_INFO_URL = 'https://api.hyperliquid.xyz/info';
const HL_MAX_CANDLES_PER_REQ = 5000;
const ALL_MIDS_TTL_MS = 3_000;

const allMidsCache = new Map<string, { ts: number; data: Record<string, string | number> }>();
const allMidsInflight = new Map<string, Promise<Record<string, string | number>>>();

function allMidsCacheKey(dex: string): string {
  return dex ? dex.toLowerCase() : '';
}

async function fetchAllMidsRaw(
  dex: string,
): Promise<{ data: Record<string, string | number>; cached: boolean }> {
  const key = allMidsCacheKey(dex);
  const hit = allMidsCache.get(key);
  if (hit && Date.now() - hit.ts < ALL_MIDS_TTL_MS) {
    return { data: hit.data, cached: true };
  }
  const pending = allMidsInflight.get(key);
  if (pending) {
    return { data: await pending, cached: true };
  }
  const p = hlInfoPost<unknown>(
    dex ? { type: 'allMids', dex } : { type: 'allMids' },
  ).then((raw) => {
    const data = unwrapAllMidsPayload(raw);
    allMidsCache.set(key, { ts: Date.now(), data });
    return data;
  });
  allMidsInflight.set(key, p);
  try {
    return { data: await p, cached: false };
  } finally {
    if (allMidsInflight.get(key) === p) allMidsInflight.delete(key);
  }
}

const INTERVAL_MS: Record<string, number> = {
  '1m': 60_000,
  '3m': 3 * 60_000,
  '5m': 5 * 60_000,
  '15m': 15 * 60_000,
  '30m': 30 * 60_000,
  '1h': 60 * 60_000,
  '2h': 2 * 60 * 60_000,
  '4h': 4 * 60 * 60_000,
  '8h': 8 * 60 * 60_000,
  '12h': 12 * 60 * 60_000,
  '1d': 24 * 60 * 60_000,
  '3d': 3 * 24 * 60 * 60_000,
  '1w': 7 * 24 * 60 * 60_000,
  '1M': 30 * 24 * 60 * 60_000,
};

export type OroCandle = {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
};

async function hlInfoPost<T = any>(body: Record<string, unknown>): Promise<T> {
  const res = await fetch(HL_INFO_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HL info ${res.status}`);
  return (await res.json()) as T;
}

/** Instant mark from the already-loaded universe tape (core + HIP-3). */
export function peekOroTapeMid(coinOrMarket: string): number {
  const tape = peekOroUniverseTape(true);
  if (!tape?.length) return NaN;
  const raw = String(coinOrMarket || '').trim().toUpperCase();
  if (!raw) return NaN;
  const { dex, sym } = coinToAllMidsQuery(raw);
  const dexCoin = dex ? `${dex.toUpperCase()}:${sym}` : '';
  const hit = tape.find((r) => {
    const id = r.id.toUpperCase();
    const c = r.coin.toUpperCase();
    const s = r.symbol.toUpperCase();
    return (
      id === raw ||
      c === raw ||
      s === raw ||
      c === sym ||
      id === `${sym}-PERP` ||
      (dexCoin && (c === dexCoin || id === dexCoin))
    );
  });
  return hit?.mid != null ? parseMidValue(hit.mid) : NaN;
}

/** Mid price for a coin like BTC or xyz:SILVER. */
export async function fetchOroMidPrice(coin: string): Promise<{ mid: number; rttMs: number; cached?: boolean }> {
  const t0 = performance.now();
  const raw = String(coin || '').trim();
  if (!raw) return { mid: NaN, rttMs: 0 };

  const tapeMid = peekOroTapeMid(raw);
  if (Number.isFinite(tapeMid)) {
    return { mid: tapeMid, rttMs: 0, cached: true };
  }

  const { dex } = coinToAllMidsQuery(raw);
  const { data, cached } = await fetchAllMidsRaw(dex);
  const rttMs = cached ? 0 : Math.round(performance.now() - t0);
  return { mid: lookupMidInMids(data, raw), rttMs, cached };
}

export async function fetchOroAllMids(): Promise<Record<string, number>> {
  const { data } = await fetchAllMidsRaw('');
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(data || {})) {
    const n = typeof v === 'string' ? parseFloat(v) : Number(v);
    if (Number.isFinite(n) && n > 0) out[k.toUpperCase()] = n;
  }
  return out;
}

const CANDLE_TTL_MS = 25_000;
/** Chartographer stays interactive above this; stitching HL’s 5k-bar windows made the plot lag. */
const MAX_ORO_TRADE_CHART_BARS = 1500;
const candleMem = new Map<string, { ts: number; candles: OroCandle[] }>();

function candleCacheKey(pair: string, interval: string, lookback: number): string {
  return `${pair}|${interval}|${lookback}`;
}

function tradeChartLookbackMs(interval: string, requestedMs?: number): number {
  const barMs = INTERVAL_MS[interval] ?? 60 * 60_000;
  const want = requestedMs ?? oroCandleLookbackMs(interval);
  return Math.min(want, MAX_ORO_TRADE_CHART_BARS * barMs);
}

export function peekOroCandles(pair: string, interval: string, lookbackMs?: number): OroCandle[] | null {
  const p = String(pair || '').trim();
  const tf = String(interval || '1h').trim();
  if (!p) return null;
  const lookback = tradeChartLookbackMs(tf, lookbackMs);
  const hit = candleMem.get(candleCacheKey(p, tf, lookback));
  if (!hit?.candles.length) return null;
  if (Date.now() - hit.ts > 5 * 60_000) return null;
  return hit.candles;
}

export async function fetchOroCandles(opts: {
  /** Market label (BTC-USD, xyz:SILVER, SILVER-USDC, …) — not a bare coin symbol. */
  pair: string;
  interval?: string;
  lookbackMs?: number;
}): Promise<{ candles: OroCandle[]; rttMs: number }> {
  const t0 = performance.now();
  const pair = String(opts.pair || '').trim();
  const interval = String(opts.interval || '1h').trim() as HLCandleInterval;
  if (!pair) return { candles: [], rttMs: 0 };
  const lookback = tradeChartLookbackMs(interval, opts.lookbackMs);

  const memKey = candleCacheKey(pair, interval, lookback);
  const cached = candleMem.get(memKey);
  if (cached?.candles.length && Date.now() - cached.ts < CANDLE_TTL_MS) {
    return { candles: cached.candles, rttMs: Math.round(performance.now() - t0) };
  }

  const endTime = Date.now();
  const startTime = endTime - lookback;
  const barMs = INTERVAL_MS[interval] ?? 60 * 60_000;
  const maxWindowMs = HL_MAX_CANDLES_PER_REQ * barMs;

  const segments: Array<{ start: number; end: number }> = [];
  for (let segEnd = endTime; segEnd > startTime; ) {
    const segStart = Math.max(startTime, segEnd - maxWindowMs);
    segments.unshift({ start: segStart, end: segEnd });
    if (segStart <= startTime) break;
    segEnd = segStart;
  }

  const chunks = await Promise.all(
    segments.map(({ start, end }) =>
      fetchTradeChartCandles({
        pair,
        interval,
        startTimeMs: start,
        endTimeMs: end,
      }).catch(() => ({ candles: [] as Array<{ time: number; open: number; high: number; low: number; close: number }> })),
    ),
  );

  const byTime = new Map<number, OroCandle>();
  for (const { candles } of chunks) {
    for (const c of candles) {
      if (!byTime.has(c.time)) {
        byTime.set(c.time, {
          time: c.time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        });
      }
    }
  }

  const candles = [...byTime.values()].sort((a, b) => a.time - b.time);
  const trimmed =
    candles.length > MAX_ORO_TRADE_CHART_BARS
      ? candles.slice(-MAX_ORO_TRADE_CHART_BARS)
      : candles;
  if (trimmed.length) candleMem.set(memKey, { ts: Date.now(), candles: trimmed });
  return { candles: trimmed, rttMs: Math.round(performance.now() - t0) };
}

/** HL candleSnapshot intervals used by the Oroboros Trade chart (Chartographer MarketBar). */
export const ORO_CHART_TIMEFRAMES = [
  '1m', '3m', '5m', '15m', '30m',
  '1h', '2h', '4h', '8h', '12h',
  '1d', '3d', '1w', '1M',
] as const;
export type OroChartTimeframe = (typeof ORO_CHART_TIMEFRAMES)[number];

export function isOroChartTimeframe(v: string): v is OroChartTimeframe {
  return (ORO_CHART_TIMEFRAMES as readonly string[]).includes(v);
}

/** Lookback window sized for a usable bar count per interval (MiniApp Trade parity). */
export function oroCandleLookbackMs(interval: string): number {
  const day = 24 * 60 * 60_000;
  switch (interval) {
    case '1m':
    case '3m':
      return 10 * day;
    case '5m':
    case '15m':
    case '30m':
      return 21 * day;
    case '1h':
    case '2h':
    case '4h':
    case '8h':
    case '12h':
      return 400 * day;
    case '1d':
    case '3d':
    case '1w':
    case '1M':
      return 800 * day;
    default:
      return 400 * day;
  }
}

export function oroTimeframeLabel(interval: string): string {
  return String(interval || '').toUpperCase();
}

export type OroAssetCtx = {
  coin: string;
  markPx: number | null;
  midPx: number | null;
  prevDayPx: number | null;
  dayVlmUsd: number | null;
  openInterest: number | null;
  funding: number | null;
};

let ctxCache: { ts: number; map: Map<string, OroAssetCtx> } | null = null;
const CTX_TTL_MS = 12_000;

/** Core HL asset contexts (mark, funding, 24h vol) via metaAndAssetCtxs. */
export async function fetchOroAssetContexts(): Promise<Map<string, OroAssetCtx>> {
  if (ctxCache && Date.now() - ctxCache.ts < CTX_TTL_MS) return ctxCache.map;
  const data = await hlInfoPost<any>({ type: 'metaAndAssetCtxs' });
  const meta = Array.isArray(data) ? data[0] : null;
  const ctxs = Array.isArray(data) ? data[1] : null;
  const universe = meta?.universe;
  const map = new Map<string, OroAssetCtx>();
  if (Array.isArray(universe) && Array.isArray(ctxs)) {
    for (let i = 0; i < Math.min(universe.length, ctxs.length); i++) {
      const name = String(universe[i]?.name || '').trim();
      if (!name) continue;
      const c = ctxs[i] || {};
      const markPx = Number(c.markPx);
      const midPx = Number(c.midPx);
      const prevDayPx = Number(c.prevDayPx);
      const dayNtlVlm = Number(c.dayNtlVlm);
      const dayBaseVlm = Number(c.dayBaseVlm);
      const oi = Number(c.openInterest);
      const funding = Number(c.funding);
      const mark = Number.isFinite(markPx) ? markPx : null;
      const asset: OroAssetCtx = {
        coin: name,
        markPx: mark,
        midPx: Number.isFinite(midPx) ? midPx : null,
        prevDayPx: Number.isFinite(prevDayPx) ? prevDayPx : null,
        dayVlmUsd: Number.isFinite(dayNtlVlm)
          ? dayNtlVlm
          : Number.isFinite(dayBaseVlm) && mark != null
            ? dayBaseVlm * mark
            : null,
        openInterest:
          Number.isFinite(oi) && mark != null ? oi * mark : Number.isFinite(oi) ? oi : null,
        funding: Number.isFinite(funding) ? funding : null,
      };
      map.set(name.toUpperCase(), asset);
    }
  }
  ctxCache = { ts: Date.now(), map };
  return map;
}

export async function fetchOroAssetCtx(coin: string): Promise<OroAssetCtx | null> {
  const raw = String(coin || '').trim();
  if (raw.includes(':')) {
    const tape =
      tapeCache?.rows && Date.now() - tapeCache.ts < TAPE_STALE_MS
        ? tapeCache.rows
        : await fetchOroUniverseTape({ includeHip3: true });
    const hit = tape.find((r) => r.coin.toUpperCase() === raw.toUpperCase());
    if (!hit) return null;
    return {
      coin: hit.coin,
      markPx: hit.mid,
      midPx: hit.mid,
      prevDayPx:
        hit.mid != null && hit.changePct != null
          ? hit.mid / (1 + hit.changePct / 100)
          : null,
      dayVlmUsd: hit.volume,
      openInterest: hit.openInterest,
      funding: null,
    };
  }
  const map = await fetchOroAssetContexts();
  const key = raw
    .toUpperCase()
    .replace(/-PERP$/, '')
    .replace(/-USD$/, '');
  return map.get(key) || null;
}

export function dayChangePct(ctx: OroAssetCtx | null): number | null {
  if (!ctx?.prevDayPx || !(ctx.prevDayPx > 0)) return null;
  const px = ctx.midPx ?? ctx.markPx;
  if (px == null || !Number.isFinite(px)) return null;
  return ((px - ctx.prevDayPx) / ctx.prevDayPx) * 100;
}

export type OroTapeKind = 'perp' | 'hip3';

export type OroTapeRow = {
  id: string;
  coin: string;
  symbol: string;
  display: string;
  kind: OroTapeKind;
  dex?: string;
  quote?: string;
  tag: 'PERP' | 'HIP-3';
  mid: number | null;
  changePct: number | null;
  volume: number | null;
  openInterest: number | null;
};

const HIP3_DEXS = ['xyz', 'cash', 'km', 'para'] as const;

const DEX_QUOTE_FALLBACK: Record<string, string> = {
  xyz: 'USDC',
  cash: 'USDT',
  km: 'USDH',
  para: 'USDC',
};

let tapeCache: { ts: number; includeHip3: boolean; rows: OroTapeRow[] } | null = null;
let tapeInFlight: { includeHip3: boolean; p: Promise<OroTapeRow[]> } | null = null;
const TAPE_TTL_MS = 30_000;
const TAPE_STALE_MS = 5 * 60_000;

function parseCtxRow(
  name: string,
  c: any,
): {
  mid: number | null;
  changePct: number | null;
  volume: number | null;
  openInterest: number | null;
} {
  const markPx = Number(c?.markPx);
  const midPx = Number(c?.midPx);
  const prevDayPx = Number(c?.prevDayPx);
  const dayNtlVlm = Number(c?.dayNtlVlm);
  const oi = Number(c?.openInterest);
  const mid = Number.isFinite(midPx) && midPx > 0 ? midPx : Number.isFinite(markPx) && markPx > 0 ? markPx : null;
  const changePct =
    mid != null && Number.isFinite(prevDayPx) && prevDayPx > 0
      ? ((mid - prevDayPx) / prevDayPx) * 100
      : null;
  const volume = Number.isFinite(dayNtlVlm) ? dayNtlVlm : null;
  const openInterest =
    Number.isFinite(oi) && mid != null ? oi * mid : Number.isFinite(oi) ? oi : null;
  return { mid, changePct, volume, openInterest };
}

async function fetchDexTape(dex: string): Promise<OroTapeRow[]> {
  const data = await hlInfoPost<any>({ type: 'metaAndAssetCtxs', dex });
  const meta = Array.isArray(data) ? data[0] : null;
  const ctxs = Array.isArray(data) ? data[1] : null;
  const universe = Array.isArray(meta?.universe) ? meta.universe : [];
  const contexts = Array.isArray(ctxs) ? ctxs : [];
  const quote = DEX_QUOTE_FALLBACK[dex] || 'USDC';
  const rows: OroTapeRow[] = [];
  for (let i = 0; i < universe.length; i++) {
    const rawName = String(universe[i]?.name || '').trim();
    if (!rawName) continue;
    const coin = rawName.includes(':') ? rawName : `${dex}:${rawName}`;
    const base = coin.includes(':') ? coin.split(':').slice(1).join(':') : coin;
    const parsed = parseCtxRow(coin, contexts[i] || {});
    rows.push({
      id: coin,
      coin,
      symbol: `${base}-${quote}`,
      display: `${base}/${quote}`,
      kind: 'hip3',
      dex,
      quote,
      tag: 'HIP-3',
      ...parsed,
    });
  }
  return rows;
}

async function loadUniverseTape(includeHip3: boolean): Promise<OroTapeRow[]> {
  const [coreMap, ...hip3Chunks] = await Promise.all([
    fetchOroAssetContexts(),
    ...(includeHip3
      ? HIP3_DEXS.map((dex) => fetchDexTape(dex).catch(() => [] as OroTapeRow[]))
      : []),
  ]);
  const rows: OroTapeRow[] = [];
  for (const ctx of coreMap.values()) {
    const name = ctx.coin;
    if (!name || name.includes(':')) continue;
    const changePct = dayChangePct(ctx);
    rows.push({
      id: `${name.toUpperCase()}-PERP`,
      coin: name.toUpperCase(),
      symbol: name.toUpperCase(),
      display: name.toUpperCase(),
      kind: 'perp',
      tag: 'PERP',
      mid: ctx.midPx ?? ctx.markPx,
      changePct,
      volume: ctx.dayVlmUsd,
      openInterest: ctx.openInterest,
    });
  }
  for (const chunk of hip3Chunks) rows.push(...chunk);
  rows.sort((a, b) => (b.volume || 0) - (a.volume || 0));
  tapeCache = { ts: Date.now(), includeHip3, rows };
  return rows;
}

/** Core perps + HIP-3 dex tapes with mid / 24h / volume for the market picker. */
export async function fetchOroUniverseTape(opts?: {
  includeHip3?: boolean;
}): Promise<OroTapeRow[]> {
  const includeHip3 = opts?.includeHip3 !== false;
  if (
    tapeCache &&
    tapeCache.includeHip3 === includeHip3 &&
    Date.now() - tapeCache.ts < TAPE_TTL_MS
  ) {
    return tapeCache.rows;
  }
  if (
    tapeCache &&
    tapeCache.includeHip3 === includeHip3 &&
    Date.now() - tapeCache.ts < TAPE_STALE_MS
  ) {
    void refreshUniverseTape(includeHip3);
    return tapeCache.rows;
  }
  return refreshUniverseTape(includeHip3);
}

function refreshUniverseTape(includeHip3: boolean): Promise<OroTapeRow[]> {
  if (tapeInFlight && tapeInFlight.includeHip3 === includeHip3) return tapeInFlight.p;
  const p = loadUniverseTape(includeHip3).finally(() => {
    if (tapeInFlight?.p === p) tapeInFlight = null;
  });
  tapeInFlight = { includeHip3, p };
  return p;
}

export function peekOroUniverseTape(includeHip3 = true): OroTapeRow[] | null {
  if (!tapeCache?.rows.length) return null;
  if (includeHip3 && !tapeCache.includeHip3) return null;
  if (Date.now() - tapeCache.ts > TAPE_STALE_MS) return null;
  return tapeCache.rows;
}
